import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Download, Smartphone, Check, Share } from 'lucide-react';
import { cn } from '@/lib/utils';
import { DownloadLinks } from '@/components/DownloadLinks';

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

const InstallPage = () => {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [isIOS, setIsIOS] = useState(false);

  useEffect(() => {
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches
      || (navigator as any).standalone === true;
    setIsInstalled(isStandalone);

    setIsIOS(/iPad|iPhone|iPod/.test(navigator.userAgent));

    const handler = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    await deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === 'accepted') setIsInstalled(true);
    setDeferredPrompt(null);
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <Card className="max-w-md w-full bg-card border-border">
        <CardContent className="p-8 text-center space-y-6">
          <div className="w-16 h-16 mx-auto rounded-2xl bg-primary/10 flex items-center justify-center">
            <Smartphone className="w-8 h-8 text-primary" />
          </div>

          <div>
            <h1 className="text-2xl font-bold font-heading text-foreground">Install Sabotage Fantasy</h1>
            <p className="text-muted-foreground mt-2 text-sm">
              Add to your home screen for the fastest game-day experience: live scores, one-tap sabotages, instant roster moves.
            </p>
          </div>

          {isInstalled ? (
            <div className="flex items-center justify-center gap-2 text-primary">
              <Check className="w-5 h-5" />
              <span className="font-medium">Already installed!</span>
            </div>
          ) : isIOS ? (
            <DownloadLinks badgeClassName="h-12" />

          ) : deferredPrompt ? (
            <Button onClick={handleInstall} size="lg" className="w-full gap-2">
              <Download className="w-5 h-5" />
              Install App
            </Button>
          ) : (
            <div className="space-y-3 text-left">
              <p className="text-sm font-medium text-foreground">On Android:</p>
              <div className="space-y-2">
                <Step num={1} text='Open browser menu (⋮)' />
                <Step num={2} text='Tap "Add to Home Screen" or "Install App"' />
              </div>
            </div>
          )}

          <div className="grid grid-cols-3 gap-3 pt-4 border-t border-border">
            <Perk emoji="⚡" label="Instant load" />
            <Perk emoji="📴" label="Works offline" />
            <Perk emoji="🔔" label="Game alerts" />
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

const Step = ({ num, text, icon }: { num: number; text: string; icon?: React.ReactNode }) => (
  <div className="flex items-center gap-3 p-2 rounded-lg bg-muted/30">
    <span className="w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xs font-bold shrink-0">
      {num}
    </span>
    <span className="text-sm text-foreground">{text}</span>
    {icon && <span className="ml-auto text-muted-foreground">{icon}</span>}
  </div>
);

const Perk = ({ emoji, label }: { emoji: string; label: string }) => (
  <div className="text-center">
    <span className="text-xl">{emoji}</span>
    <p className="text-[10px] text-muted-foreground mt-1">{label}</p>
  </div>
);

export default InstallPage;
